import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, UseFormSetValue, Control } from 'react-hook-form';
import _ from 'lodash';

// GraphQL imports
import { FeedItemType, RecCategory, FeedItemOrderBy } from 'shared/__generated__/graphql';

// Hooks and utilities
import { usePrevious } from 'shared/misc/hooks/usePrevious';
import { useRouter, useRouterState } from '@tanstack/react-router';
import { FeedContentFilter } from './useGenericFeedInfiniteScroll';

export const FEED_ITEM_ORDER_BY_LABELS: Record<string, string> = {
  [FeedItemOrderBy.NewestFirst]: 'Latest',
  [FeedItemOrderBy.Hot]: 'Hot',
  [FeedItemOrderBy.Random]: 'Random',
  [FeedItemOrderBy.Rising]: 'Rising',
  [FeedItemOrderBy.TopOfDay]: 'Top of Day',
  [FeedItemOrderBy.TopOfWeek]: 'Top of Week',
  [FeedItemOrderBy.TopOfMonth]: 'Top of Month',
  [FeedItemOrderBy.TopOfYear]: 'Top of Year',
  [FeedItemOrderBy.TopOfAllTime]: 'Top of all time',
};

export const ENABLED_FEED_ITEM_TYPES_FOR_CATEGORY_FILTER = [
  FeedItemType.AddRecommendation,
  FeedItemType.NewPrompt,
  FeedItemType.NewRePostWithContent,
  FeedItemType.NewPromptRecReply,
];

export const DISABLED_FEED_ITEM_TYPES_FOR_SORT_BY_FILTER = [
  FeedItemType.NewUser,
  FeedItemType.NewComment,
  FeedItemType.NewUserFollow,
  FeedItemType.NewRePost,
];

const DEFAULT_FEED_ITEM_TYPES = [
  FeedItemType.AddRecommendation,
  FeedItemType.NewPrompt,
  FeedItemType.NewRePost,
  FeedItemType.NewRePostWithContent,
  FeedItemType.NewPromptRecReply,
];

const REC_ONLY_FEED_ITEM_TYPES = [
  FeedItemType.AddRecommendation,
  FeedItemType.NewRePost,
  FeedItemType.NewRePostWithContent,
  FeedItemType.NewPromptRecReply,
];

const ASK_ONLY_FEED_ITEM_TYPES = [FeedItemType.NewPrompt];

const DEFAULT_ORDER_BY = FeedItemOrderBy.NewestFirst;

export interface UseFeedToolbarUtilProps {
  onlyFollowing?: boolean;
  isInView?: boolean;
  filter?: FeedContentFilter;
  originUserId?: string;
  isRandomSelection?: boolean;
  defaultTypes?: FeedItemType[];
  forceTypes?: FeedItemType[] | null;
  includeOriginUsername?: boolean;
  includeQuickFilters?: boolean;
  orderBy?: FeedItemOrderBy;
  enabledOrderByOptions?: FeedItemOrderBy[];
  enabledFeedItemTypes?: FeedItemType[];
  shouldDebounceVariables?: boolean;
  navigate?: ({ resetScroll, to }: { resetScroll: boolean; to: string }) => void;
  pathname?: string;
  initialIsInline?: any;
  isWebEnvironment?: boolean;
}

type FeedFilterValues = {
  category: RecCategory | null;
  feedItemTypes: FeedItemType[];
  orderBy: FeedItemOrderBy;
};

interface UseFeedToolbarUtilReturn {
  // Form state
  control: Control<FeedFilterValues>;
  values: FeedFilterValues;
  setValue: UseFormSetValue<FeedFilterValues>;
  toggleOnlyAsks: () => void;
  toggleOnlyRecs: () => void;
  toggleAll: () => void;
  // Computed values
  variables: {
    showGlobalFeed: boolean;
    types: FeedItemType[];
    category: RecCategory | null;
    originUserId?: string;
    isRandomSelection?: boolean;
    orderBy: FeedItemOrderBy;
  };
  isNonDefaultFilter: boolean;
  isInline: boolean;
  isIncompatibleFeedType: boolean;
  isNonDefaultSortBy: boolean;
  isOnlyRecs: boolean;
  isOnlyAsks: boolean;

  // Labels and constants
  FEED_ITEM_ORDER_BY_LABELS: typeof FEED_ITEM_ORDER_BY_LABELS;
  ENABLED_FEED_ITEM_TYPES_FOR_CATEGORY_FILTER: typeof ENABLED_FEED_ITEM_TYPES_FOR_CATEGORY_FILTER;
  DISABLED_FEED_ITEM_TYPES_FOR_SORT_BY_FILTER: typeof DISABLED_FEED_ITEM_TYPES_FOR_SORT_BY_FILTER;

  // Actions
  toggleInlineView: () => void;
}

export function useFeedToolbarUtils({
  onlyFollowing,
  forceTypes,
  enabledFeedItemTypes = [],
  navigate,
  pathname,
  originUserId,
  enabledOrderByOptions,
  isRandomSelection,
  shouldDebounceVariables = true,
  initialIsInline = false,
  isWebEnvironment = true,
  orderBy: initialOrderBy = DEFAULT_ORDER_BY,
}: UseFeedToolbarUtilProps): UseFeedToolbarUtilReturn {
  const [isInline, setIsInline] = useState<boolean>(initialIsInline);

  const enabledOrderByOptionSet = useMemo(() => {
    return new Set(enabledOrderByOptions || Object.keys(FEED_ITEM_ORDER_BY_LABELS));
  }, [enabledOrderByOptions]);

  const getInitialValues = () => {
    if (typeof window === 'undefined') {
      return {
        category: null,
        feedItemTypes: [],
        orderBy: initialOrderBy,
      };
    }

    if (isWebEnvironment) {
      const params = new URLSearchParams(window.location.search);
      const urlCategory = params.get('category') as RecCategory | null;
      const urlOrderBy = params.get('orderBy') as FeedItemOrderBy | null;
      const urlFeedItemTypes = params.get('types')?.split(',').filter(Boolean) as
        | FeedItemType[]
        | null;

      return {
        category: urlCategory || null,
        feedItemTypes: urlFeedItemTypes || [],
        orderBy:
          urlOrderBy && enabledOrderByOptionSet.has(urlOrderBy) ? urlOrderBy : initialOrderBy,
      };
    }

    return {
      category: null,
      feedItemTypes: [],
      orderBy: initialOrderBy,
    };
  };

  const { watch, control, setValue } = useForm<FeedFilterValues>({
    defaultValues: getInitialValues(),
  });

  const values = watch();
  const { category, feedItemTypes: selectedFeedItemTypes, orderBy: selectedOrderBy } = values;

  const prevValues = usePrevious(values);

  const isIncompatibleFeedType = selectedFeedItemTypes.some((type) =>
    DISABLED_FEED_ITEM_TYPES_FOR_SORT_BY_FILTER.includes(type),
  );

  useEffect(() => {
    if (!prevValues) return;
    if (
      prevValues?.orderBy !== selectedOrderBy ||
      prevValues?.category !== category ||
      prevValues?.feedItemTypes !== selectedFeedItemTypes
    ) {
      if (
        isIncompatibleFeedType &&
        (values.category !== null || values.orderBy !== FeedItemOrderBy.NewestFirst)
      ) {
        setValue('orderBy', FeedItemOrderBy.NewestFirst);
        setValue('category', null);
      }
    }
  }, [values]);

  const prevSearchString = useRef(isWebEnvironment ? window.location.search : '');

  const [debouncedVariables, setDebouncedVariables] = useState(values);

  const updateDebouncedVariables = useMemo(() => {
    return _.debounce((v: FeedFilterValues) => {
      if (isWebEnvironment && navigate && pathname) {
        const params = new URLSearchParams(window.location.search);
        const { category, feedItemTypes, orderBy } = v;

        // Only add params that differ from defaults
        if (category) {
          params.set('category', category);
        } else {
          params.delete('category');
        }

        if (orderBy && orderBy !== DEFAULT_ORDER_BY) {
          params.set('orderBy', orderBy);
        } else {
          params.delete('orderBy');
        }

        const hasNonDefaultTypes = feedItemTypes.length > 0;

        if (hasNonDefaultTypes) {
          params.set('types', feedItemTypes.join(','));
        } else {
          params.delete('types');
        }

        const newSearchString = params.toString();
        if (newSearchString !== prevSearchString.current) {
          navigate({
            resetScroll: false,
            to: `${pathname}${params.toString() ? `?${params.toString()}` : ''}`,
          });
        }
        prevSearchString.current = newSearchString;
      }

      setDebouncedVariables(v);
    }, 1000);
  }, [pathname]);

  // Update URL when filters change
  useEffect(() => {
    updateDebouncedVariables(values);
  }, [category, selectedFeedItemTypes, selectedOrderBy]);

  const variables = useMemo(() => {
    const v = shouldDebounceVariables ? debouncedVariables : values;

    return {
      showGlobalFeed: !onlyFollowing,
      types:
        v.feedItemTypes?.length > 0
          ? [...v.feedItemTypes, ...(forceTypes || [])]
          : [
              ...(forceTypes || []),
              FeedItemType.AddRecommendation,
              FeedItemType.NewRePost,
              FeedItemType.NewRePostWithContent,
              FeedItemType.NewPromptRecReply,
              FeedItemType.NewPrompt,
            ],
      category: v.category ? (v.category.toUpperCase() as RecCategory) : null,
      originUserId,
      isRandomSelection,
      orderBy: v.orderBy || initialOrderBy,
    };
  }, [
    onlyFollowing,
    category,
    selectedFeedItemTypes,
    originUserId,
    isRandomSelection,
    selectedOrderBy,
    initialOrderBy,
    shouldDebounceVariables,
    debouncedVariables,
  ]);

  const isNonDefaultFilter = useMemo(() => {
    return values.category !== null || values.feedItemTypes.length > 0;
  }, [values]);

  const isNonDefaultSortBy = useMemo(() => {
    return values.orderBy !== FeedItemOrderBy.NewestFirst;
  }, [values]);

  const toggleInlineView = useCallback(() => {
    setIsInline((prev) => {
      const newState = !prev;
      return newState;
    });
  }, [setIsInline]);

  const toggleOnlyAsks = useCallback(() => {
    setValue('feedItemTypes', [...ASK_ONLY_FEED_ITEM_TYPES]);
  }, [setValue]);

  const toggleOnlyRecs = useCallback(() => {
    setValue('feedItemTypes', [...REC_ONLY_FEED_ITEM_TYPES]);
  }, [setValue]);

  const toggleAll = useCallback(() => {
    setValue('feedItemTypes', []);
  }, [setValue]);

  const isOnlyRecs = useMemo(() => {
    return (
      selectedFeedItemTypes.length === REC_ONLY_FEED_ITEM_TYPES.length &&
      selectedFeedItemTypes.every((type) => REC_ONLY_FEED_ITEM_TYPES.includes(type))
    );
  }, [selectedFeedItemTypes]);

  const isOnlyAsks = useMemo(() => {
    return (
      selectedFeedItemTypes.length === ASK_ONLY_FEED_ITEM_TYPES.length &&
      selectedFeedItemTypes.every((type) => ASK_ONLY_FEED_ITEM_TYPES.includes(type))
    );
  }, [selectedFeedItemTypes]);

  return {
    // Form state
    control,
    values,
    setValue,

    // Computed values
    variables,
    isNonDefaultFilter,
    isNonDefaultSortBy,
    isInline,
    isIncompatibleFeedType,

    isOnlyRecs,
    isOnlyAsks,

    // Actions
    toggleInlineView,
    toggleOnlyAsks,
    toggleOnlyRecs,
    toggleAll,
  };
}
